﻿import CatalogMappingGrids from "./CatalogMappingGrids";
import React, { useEffect, useRef, useState } from "react";
import {
  clearPendingLocalChanges,
  saveNewMappings,
  selectMappedSourceCatalogs,
  selectMapOnFlyInfo,
  setIsMapOnFlyOpen,
  setCatalogFilter,
  selectIsAddCrossRefOpen,
  resetCatalogFilter,
  selectConnectionCatalogs,
  selectMissingCrossRefCounts,
  saveNewCrossReferenceMappings,
  setSelectedXrefType,
  fetchChildSourceCatalogs,
  fetchTargetConnectionCatalogs,
  fetchMappedCatalogs,
  selectSourceGridLoaded,
  selectTargetGridLoaded,
} from "@/features/connections/crossReferenceSlice";
import {
  Box,
  Button,
  TextField,
  Container,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import MappedCrossReferencesGrid from "./MappedCrossReferencesGrid";
import { selectWorkflow, statusTypes, statusLabels } from "@/features/homepage/workflowManagerSessionSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { fetchCrossReferenceSummaries, selectCrossReferenceSummarySelectedTab, clearTabCache } from "@/features/maintenance/maintenanceSlice";
import { getRequeueStatus } from "@/components/Maintenance/MapOnFly/MapOnFly";
import {
  setIsWorkflowStepsOpen,
  setWorkflowStepsInfo,
  updateWorkflowStatus,
  fetchMissingCrossReferenceData,
} from "@/features/homepage/workflowManagerSlice";
import DetailDisplay from "@/components/Maintenance/DetailDisplay";
import {
  composeSourceParentCatalogList,
  MappingTypes,
} from "@/utils/utils";

const UOM_VERTICAL_OFFSET_PX = 140;
// Add a small extra gap below the grid for the details
const DETAILS_EXTRA_SPACE_PX = 24;

const UOMConversionDisplay = ({
  crossRefProperties = {
    sourceCatalog: { ConversionFactor: 1 },
    targetCatalog: { ConversionFactor: 1 },
  },
  setCrossRefProperties = () => {},
}) => {
  const onValueChange = ({ value, connectionType }) => {
    const currentProperties = {
      ...crossRefProperties,
      [connectionType]: { ConversionFactor: value },
    };
    setCrossRefProperties(currentProperties);
  };
  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        width: "100%",
        mt: `-${UOM_VERTICAL_OFFSET_PX}px`, // lift into the middle of the grids to keep prior ui
        position: "relative",
        zIndex: 10, // keep above the grids
      }}
    >
      <Container sx={{ width: "50%" }}>
        <TextField
          sx={{ width: "80px" }}
          value={crossRefProperties?.sourceCatalog?.ConversionFactor ?? 1}
          onChange={(event) => {
            onValueChange({
              value: event.target.value,
              connectionType: "sourceCatalog",
            });
          }}
        />
      </Container>
      <Container sx={{ width: "50%" }}>
        <TextField
          sx={{ width: "80px" }}
          value={crossRefProperties?.targetCatalog?.ConversionFactor ?? 1}
          onChange={(event) => {
            onValueChange({
              value: event.target.value,
              connectionType: "targetCatalog",
            });
          }}
        />
      </Container>
    </Container>
  );
};

const ItemUOMCatalogTab = () => {
  const dispatch = useDispatch();

  const mapOnFlyInfo = useSelector(selectMapOnFlyInfo);
  const workflow = useSelector(selectWorkflow);
  const mappedCatalogs = useSelector(selectMappedSourceCatalogs);
  const isAddCrossRef = useSelector(selectIsAddCrossRefOpen);
  const [mappedParentCatalogs, setMappedParentCatalogs] = useState([]); // Mapped "Item" cross refs
  const unmappedCatalogCounts = useSelector(selectMissingCrossRefCounts);
  const catalogInformation = useSelector(selectConnectionCatalogs);
  const selectedTab = useSelector(selectCrossReferenceSummarySelectedTab);
  const sourceGridLoaded = useSelector(selectSourceGridLoaded);
  const targetGridLoaded = useSelector(selectTargetGridLoaded);

  const [isRowSelected, setIsRowSelected] = useState(false);
  const [selectedParentSourceCatalogId, setSelectedParentSourceCatalogId] = useState(null);
  const [selectedSourceCatalogMappingType, setSelectedSourceCatalogMappingType] = useState(null);
  useEffect(() => {
    dispatch(resetCatalogFilter({}));
  }, []);

  const identifiedUOMCatalogs = isAddCrossRef
    ? null
    : mapOnFlyInfo?.crossReferenceInfo.IdentifiedCatalogs.filter(
        (x) => x.ReferenceType === 2
      );

  const [canRequeue, setCanRequeue] = useState(false);

  const [sourceSearchValue, setSourceSearchValue] = useState("");
  const [targetSearchValue, setTargetSearchValue] = useState("");
  const [searchValue, setSearchValue] = useState(""); // TODO: unused?

  const prevMapOnFlyInfo = useRef(null);
  useEffect(() => {
    const prev = JSON.stringify(prevMapOnFlyInfo.current);
    const current = JSON.stringify(mapOnFlyInfo);

    if (prev !== current) {
      fetchCatalogs();
      prevMapOnFlyInfo.current = mapOnFlyInfo;
    }
  }, [mapOnFlyInfo]);

  useEffect(() => {
    if (isAddCrossRef) {
      setCanRequeue(false);
    } else {
      setCanRequeue(() =>
        getRequeueStatus({
          unmappedCatalogCounts,
          referenceType: "UOM",
          catalogInformation,
          identifiedCatalogs: identifiedUOMCatalogs,
        })
      );
    }
  }, [
    unmappedCatalogCounts,
    catalogInformation,
    mapOnFlyInfo,
    mappedCatalogs,
    identifiedUOMCatalogs,
  ]);

  useEffect(() => {
    //only display children catalog of explicitly mapped parents
    const parentCatalogFilters = mappedParentCatalogs
      ?.filter((x) => x.DefaultMappingType === -1)
      .map((x) => {
        return {
          connectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
          path: "ArtifactData.ItemId",
          value: x.CatalogId,
        };
      });

    let nonExplicitMappings = [
      {
        connectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
        path: "DefaultMappingType",
        value: 1, //ParentId stored as null in artifact data, capture default and virtual catalogs
      },
    ];
    //if parent default exists, allow child to map default as well
    if (mappedParentCatalogs?.find((x) => x.DefaultMappingType === 0)) {
      nonExplicitMappings.push({
        connectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
        path: "DefaultMappingType",
        value: 0,
      });
    }
    dispatch(
      setCatalogFilter({
        filter: {
          sourceFilter: [...parentCatalogFilters, ...nonExplicitMappings],
        },
      })
    );
  }, [mappedParentCatalogs, catalogInformation]);

  const fetchCatalogs = async () => {
    const info = mapOnFlyInfo.crossReferenceInfo;
    dispatch(setSelectedXrefType({ selectedXref: "UOM" }));
    await dispatch(
      fetchMappedCatalogs({
        connectionPairId: info.ConnectionPairId,
        crossReferenceType: "UOM",
        sourceConnectionId: info.SourceConnectionId,
        targetConnectionId: info.TargetConnectionId,
      })
    )
    await dispatch(
      fetchChildSourceCatalogs({
        parentType: "Item",
        childType: "UOM",
        setMappedParentCatalogs,
        searchFilter: sourceSearchValue,
      })
    )
  }

  const fetchTargetCatalogsWithParentSources = ({
    connectionPairId, 
    crossReferenceType, 
    sourceConnectionId,
    targetConnectionId,
    searchFilter = null,
  }) => {
    const sourceCatalogIdList = composeSourceParentCatalogList(
      isRowSelected, 
      selectedParentSourceCatalogId, 
      selectedSourceCatalogMappingType, 
      mappedParentCatalogs,
    );
    return fetchTargetConnectionCatalogs({
      connectionPairId,
      crossReferenceType,
      sourceConnectionId,
      targetConnectionId,
      searchFilter: searchFilter != null ? searchFilter : targetSearchValue,
      sourceCatalogIdList,
      parentIsDefault: (!isRowSelected || !selectedParentSourceCatalogId) && selectedSourceCatalogMappingType != MappingTypes.Default,
      clearCache: true,
    });
  }
  useEffect(() => {
    if (mappedParentCatalogs?.length === 0) {
      return
    }
    const info = mapOnFlyInfo.crossReferenceInfo;

    dispatch(
      fetchTargetCatalogsWithParentSources({
        connectionPairId: info.ConnectionPairId,
        crossReferenceType: "UOM",
        sourceConnectionId: info.SourceConnectionId,
        targetConnectionId: info.TargetConnectionId,
      })
    )
  }, [mappedParentCatalogs, mapOnFlyInfo, selectedParentSourceCatalogId, isRowSelected])

  const closeWorkflowStepsPopup = () => {
    dispatch(
      setWorkflowStepsInfo({
        workflowRecordId: "",
        sourceArtifactDictionaryDataId: "",
        connectionPairId: -1,
      })
    );
    dispatch(setIsWorkflowStepsOpen({ isOpen: false }));
  };

  const handleSave = () => {
    // heap.io
    if (isAddCrossRef) {
      window.heap.track("Cross References - Create New");
    } else {
      window.heap.track("Map on Fly - Save New Cross References");
    }

    dispatch(
      isAddCrossRef
        ? saveNewCrossReferenceMappings({
            connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            crossReferenceType: "UOM",
            sourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            targetConnectionId:
              mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
            newMappings: mappedCatalogs,
          })
        : saveNewMappings({
            connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            crossReferenceType: "UOM",
            sourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            targetConnectionId:
              mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
            newMappings: mappedCatalogs,
            usedCatalogs: identifiedUOMCatalogs,
          })
    )
      .then(unwrapResult)
      .then((response) => {
        dispatch(
          createAlert({
            status: response.data.error ? "Error" : "Success",
            message: response.data.message,
            alertType: "single",
          })
        );
      })
      .then(() => {
        if (isAddCrossRef) {
          fetchCatalogs();
          dispatch(clearTabCache(selectedTab));
          dispatch(
            fetchCrossReferenceSummaries({
              connectionPairId:
                mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
              crossReferenceType: selectedTab,
            })
          );
        } else {
          dispatch(
            fetchMissingCrossReferenceData({ currentOperationId: workflow })
          );
        }
      });
  };
  const getStatusLabel = (statusValue) => {
    const status = statusLabels[statusValue];
    return status ? status.label : "Unknown";
  };
  const handleSaveAndRequeue = () => {
    // heap.io
    window.heap.track("Map on Fly - Save and Requeue");

    dispatch(
      saveNewMappings({
        connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
        crossReferenceType: "UOM",
        sourceConnectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
        targetConnectionId: mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
        newMappings: mappedCatalogs,
        usedCatalogs: identifiedUOMCatalogs,
      })
    )
      .then(unwrapResult)
      .then((response) => {
        const workflowRowActionDTO = [
          {
            SourceDictionaryDataId:
              mapOnFlyInfo.crossReferenceInfo.SourceArtifactDictionaryDataId,
            Status: statusTypes.Error,
            IsHeld: false,
            IsArchived: false,
            SourceReference: mapOnFlyInfo.crossReferenceInfo.ReferenceId,
            OperationId: workflow,
            SourceConnectionId:
              mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
            ConnPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
            WorkflowRecordId: mapOnFlyInfo.crossReferenceWorkflowRecordId,
          },
        ];
        axios
          .post(
            "/operationsmanager/requeue-operation-task",
            workflowRowActionDTO,
            null
          )
          .then((response) => {
            dispatch(setIsMapOnFlyOpen({ value: false }));
            //SHEP-373: Replaced with new alert and close pop-up window
            dispatch(
              createAlert({
                status: response.data.alert,
                message:
                  "Successfully created Cross Reference, Workflow was successfully requeued",
                alertType: "single",
              })
            );
            closeWorkflowStepsPopup();
            //SHEP-373: refresh workflow manager tab status with processing
            // Update status via Redux dispatch to custom updateWorkflowStatus reducer
            dispatch(
              updateWorkflowStatus({
                workflowRecordId: mapOnFlyInfo.crossReferenceWorkflowRecordId,
                status: getStatusLabel(statusTypes.Processing),
              })
            );
          })
          .catch((error) => {
            console.error("Error applying option: ", error);
          });
      });
  };

  const handleRefresh = () => {
    dispatch(clearPendingLocalChanges());
    fetchCatalogs();
  };

  const onSourceGridRowClick = ({ row }) => {
    const artifactDataObj = JSON.parse(row?.ArtifactData) ?? {};
    setIsRowSelected(row ? true : false);

    setSelectedSourceCatalogMappingType(row?.DefaultMappingType);
    setSelectedParentSourceCatalogId(artifactDataObj.ItemId);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CatalogMappingGrids
          sourceConnection={mapOnFlyInfo.crossReferenceInfo.SourceConnection}
          targetConnection={mapOnFlyInfo.crossReferenceInfo.TargetConnection}
          descriptionField={"ItemCode"}
          onSourceGridRowClick={onSourceGridRowClick}
          catalogType={"UOM"}
          onSearchTextChange={setSearchValue}
          onSourceSearchTextChange={setSourceSearchValue}
          onTargetSearchTextChange={setTargetSearchValue}
          sourceGridLoaded={sourceGridLoaded}
          targetGridLoaded={targetGridLoaded}
          customFetchTargetCatalog={fetchTargetCatalogsWithParentSources}
        >
          {/* keep these as direct children only */}
          <UOMConversionDisplay />
          <DetailDisplay
            verticalOffsetPx={UOM_VERTICAL_OFFSET_PX}
            extraSpacePx={DETAILS_EXTRA_SPACE_PX}
          />
        </CatalogMappingGrids>
        <MappedCrossReferencesGrid
          sourceConnection={mapOnFlyInfo.crossReferenceInfo.SourceConnection}
          targetConnection={mapOnFlyInfo.crossReferenceInfo.TargetConnection}
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "right",
            alignItems: "center",
            padding: "5% 10% 5% 0",
          }}
        >
          <Button className="btn-primary map-on-fly-CTA" onClick={handleSave}>
            Save
          </Button>
          {!isAddCrossRef && (
            <Button
              className="btn-primary map-on-fly-CTA"
              onClick={handleSaveAndRequeue}
              disabled={!canRequeue}
            >
              Save & requeue
            </Button>
          )}
          <Button
            className="btn-primary map-on-fly-CTA"
            onClick={handleRefresh}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};
export default ItemUOMCatalogTab;
